import {FC, useEffect, useState} from 'react'
import {i18n} from '../../translate/i18n'
import {secondsToHour} from '../../utils/formater'
import {Hash, Clock, Users, User, Phone, Mail} from 'lucide-react'


enum AccessibilityOption {
  ApoioAoIdoso = 'Apoio ao Idoso',
  Cadeirante = 'Cadeirante',
  PCD = 'PCD',
  Autismo = 'Autismo',
  Gestantes = 'Gestantes ',
  Obeso = 'Obeso '
}

type QueueType = {
  id: string
  position: number
  minutesOnQueue: string
  people: number
  name: string
  phone: string
  email: string
  seconds: number
  createdAt: Date
  preferential: boolean
  accessibilityNeeds: AccessibilityOption| null
  finalized: 'confirmed' | 'removed' | null
  entryTime: string | null
  updatedAt: string | null
}

type Props = {
  queue: QueueType
  setQueueSelected: (value: any) => void
  setOption: (value: 'removed' | 'confirmed' | 'add' | 'edit') => void
  showWrapperModal: (value: string) => void
  limitMinutes: number
  onProposeAllocation?: (queue: QueueType) => void
  useTableManagement?: boolean
}

const QueueCard: FC<Props> = ({
  queue,
  setQueueSelected,
  setOption,
  showWrapperModal,
  limitMinutes,
  onProposeAllocation,
  useTableManagement = false,
}) => {
  const [newTime, setNewTime] = useState(() => {
    // Converte minutesOnQueue (HH:MM:SS) para segundos, igual webapp-nis faz
    if (queue.minutesOnQueue) {
      const parts = queue.minutesOnQueue.split(':')
      return (parseInt(parts[0]) * 3600) + (parseInt(parts[1]) * 60) + parseInt(parts[2])
    }
    return queue.seconds
  })

  useEffect(() => {
    const startTime = Date.now() / 1000
    const initialSeconds = newTime
    
    const intervalId = setInterval(() => {
      const elapsed = Math.trunc((Date.now() / 1000) - startTime)
      setNewTime(initialSeconds + elapsed)
    }, 500)

    // Limpar o intervalo quando o componente for desmontado
    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className='d-flex flex-row px-4 py-6 align-items-center'
      style={{border: '1px solid #D3D9E8', borderLeft: '4px solid #4377FF', borderRadius: 8, backgroundColor: '#FAFBFC'}}
    >
      <div className='d-flex flex-column w-50 gap-3'>
        {/* Seção de Posição ou Status */}
        {queue.finalized ? (
          <div
            style={{
              display: 'inline-block',
              padding: '6px 12px',
              backgroundColor: queue.finalized === 'confirmed' ? '#E8F5FF' : '#FFE8E8',
              border: `1px solid ${queue.finalized === 'confirmed' ? '#4377FF' : '#EB5353'}`,
              borderRadius: 6,
              width: 'fit-content',
            }}
          >
            <p style={{color: queue.finalized === 'confirmed' ? '#4377FF' : '#EB5353', margin: 0, fontSize: 12, fontWeight: 600}}>
              {i18n.t(`queue.${queue.finalized}`)} {' '}
              {queue.updatedAt ? new Date(queue.updatedAt).toLocaleString().replace(',', '') : ''}
            </p>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              backgroundColor: '#E8F3FF',
              borderRadius: 6,
              width: 'fit-content',
            }}
          >
            <Hash size={18} style={{color: '#4377FF', fontWeight: 700}} />
            <span style={{fontSize: 13, fontWeight: 600, color: '#2A2D36'}}>
              {queue.position}
            </span>
          </div>
        )}

        {/* Tempo de espera */}
        {!queue.finalized && (
          <p
            style={
              queue.minutesOnQueue.split(':')[0] &&
              queue.minutesOnQueue.split(':')[1] &&
              newTime >= limitMinutes * 60
                ? {color: '#EB5353', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}
                : {color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}
            }
          >
            <Clock size={16} color={newTime >= limitMinutes * 60 ? '#EB5353' : '#4377FF'} />
            <span style={{fontWeight: 600, color: '#2A2D36'}}>{i18n.t(`queue.time`)}</span>
            <span style={{fontWeight: 500}}>{secondsToHour(newTime)}</span>
          </p>
        )}

        {/* Pessoas */}
        <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}}>
          <Users size={16} color='#4377FF' />
          <span style={{fontWeight: 600, color: '#2A2D36'}}>
            {queue.people} {i18n.t(`queue.people`)}
          </span>
        </p>

        {/* Nome */}
        <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}}>
          <User size={16} color='#4377FF' />
          <span style={{fontWeight: 600, color: '#2A2D36'}}>{queue.name}</span>
        </p>

        {/* Telefone */}
        <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}}>
          <Phone size={16} color='#4377FF' />
          <span style={{fontWeight: 500, color: '#2A2D36'}}>{queue.phone}</span>
        </p>

        {/* Email */}
        {queue.email && (
          <p style={{color: '#6E7586', margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}}>
            <Mail size={16} color='#4377FF' />
            <span style={{fontSize: '12px', color: '#2A2D36', wordBreak: 'break-all'}}>{queue.email}</span>
          </p>
        )}

        {/* Badges de Preferencial e Acessibilidade */}
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4}}>
          {queue.preferential && (
            <div
              style={{
                padding: '6px 10px',
                border: '1px solid #EB5353',
                backgroundColor: '#FFDDDD',
                borderRadius: 6,
                display: 'inline-block',
              }}
            >
              <span style={{color: '#EB5353', fontSize: 11, fontWeight: 600}}>★ {i18n.t('queue.preferential')}</span>
            </div>
          )}
          {queue.accessibilityNeeds && (
            <div
              style={{
                padding: '6px 10px',
                border: '1px solid #9C27B0',
                backgroundColor: '#F3E5F5',
                borderRadius: 6,
                display: 'inline-block',
              }}
            >
              <span style={{color: '#9C27B0', fontSize: 11, fontWeight: 600}}>
                ♿ {queue.accessibilityNeeds}
              </span>
            </div>
          )}
        </div>
      </div>
      {!queue.finalized && (
        <div className='d-flex flex-column gap-4 w-50'>
          {useTableManagement && (
          <button
            onClick={() => onProposeAllocation?.(queue)}
            style={{
              border: '1px solid #4377FF',
              borderRadius: '4px',
              backgroundColor: '#4377FF',
              color: 'white',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#3561d9'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#4377FF'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Propor Alocação de Mesas'
          >
            <i style={{color: 'white'}} className='fa fa-chair' aria-hidden='true'></i>
          </button>
          )}
          <button
            onClick={() => {
              setQueueSelected(queue)
              setOption('confirmed')
              showWrapperModal(i18n.t(`queue.confirm`))
            }}
            style={{
              border: '1px solid #5D8ED8',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8F0FF'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Confirmar Chegada'
          >
            <i style={{color: '#5D8ED8'}} className='fa fa-check' aria-hidden='true'></i>
          </button>

          <button
            onClick={() => {
              setQueueSelected(queue)
              setOption('edit')
              showWrapperModal(i18n.t(`general.edit`))
            }}
            style={{
              border: '1px solid #5D8ED8',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8F0FF'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Editar Fila'
          >
            <i style={{color: '#5D8ED8'}} className='fa fa-pen' aria-hidden='true'></i>
          </button>

          <button
            onClick={() => {
              setQueueSelected(queue)
              setOption('removed')
              showWrapperModal(i18n.t(`queue.cancel`))
            }}
            style={{
              border: '1px solid #EB5353',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              width: 60,
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FFDDDD'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
              ;(e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'
            }}
            title='Cancelar Fila'
          >
            <i style={{color: '#EB5353'}} className='fa fa-times' aria-hidden='true'></i>
          </button>
        </div>
      )}
    </div>
  )
}

export {QueueCard}
